import { useRef, useState, useEffect } from 'react';
import { useTypekitReady } from '@utils/react';
import { setUnderlineStyling } from '../utils/setUnderlineStyling.js';

let UnderlineDirections = /*#__PURE__*/function (UnderlineDirections) {
  UnderlineDirections["VERTICAL"] = "vertical";
  UnderlineDirections["HORIZONTAL"] = "horizontal";
  return UnderlineDirections;
}({});
/**
 * Hook that ensures that the tab underline always positions itself below the
 * child of a parent element with the index matching the activeItemIndex.
 */
function useDynamicUnderlineStyling({
  activeItemIndex,
  activeItem,
  separatorOffset = 0,
  parentElementRef,
  disableInitialTransition,
  direction = UnderlineDirections.HORIZONTAL
}) {
  const typekitReady = useTypekitReady();
  const initial = useRef(true);
  const [visible, setVisible] = useState(activeItemIndex !== undefined && activeItemIndex !== -1);
  const prevIndexRef = useRef(activeItemIndex);
  useEffect(() => {
    // In case our fonts haven't loaded yet, then bail out since incorrect fonts
    // may screw up position calculations.
    if (!typekitReady) {
      return;
    }
    const parent = parentElementRef.current;
    const prevIndex = prevIndexRef.current ?? activeItemIndex ?? 0;
    if (!parent) {
      return;
    }
    const distance = Math.abs(prevIndex - (activeItemIndex ?? 0));
    const translateTransitionDuration = getSmoothedDuration(distance);
    const sizeTransitionDuration = disableInitialTransition && initial.current ? 0 : 200;

    // In case there is no active item then the underline should be hidden
    if (typeof activeItemIndex === "undefined" || activeItemIndex === -1) {
      setUnderlineStyling(parent, {
        size: 0,
        translateTransitionDuration,
        sizeTransitionDuration
      });
      setVisible(false);
      prevIndexRef.current = undefined;
      return;
    }

    // ... else we set the styling based on the currently active element
    const elements = parent.children;
    const activeElement = activeItem ?? elements[activeItemIndex];
    if (!activeElement) {
      return;
    }

    // update the underline styling when the active element changes
    const observer = new MutationObserver(() => {
      applyStylingWhenReady();
    });
    observer.observe(activeElement, {
      subtree: true,
      childList: true,
      characterData: true
    });
    let timeoutId;
    const applyStylingWhenReady = () => {
      const elementOffset = direction === UnderlineDirections.HORIZONTAL ? activeElement.offsetLeft : activeElement.offsetTop;
      const size = direction === UnderlineDirections.HORIZONTAL ? activeElement.clientWidth - (activeItemIndex === elements.length - 2 ? 0 : separatorOffset) : activeElement.clientHeight - (activeItemIndex === elements.length - 2 ? 0 : separatorOffset);
      if (elementOffset === 0 && size === 0) {
        timeoutId = window.setTimeout(applyStylingWhenReady, 100);
        return;
      }
      setUnderlineStyling(parent, {
        offset: elementOffset,
        size,
        translateTransitionDuration,
        sizeTransitionDuration
      });
      setVisible(true);
      prevIndexRef.current = activeItemIndex;
      initial.current = false;
    };
    applyStylingWhenReady();
    return () => {
      window.clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [typekitReady, activeItemIndex, parentElementRef, direction, activeItem, disableInitialTransition, separatorOffset]);
  return visible;
}

/**
 * Helper that returns a smoothed animation duration based on the index
 * distance between two elements.
 */
function getSmoothedDuration(distance) {
  return distance === 0 ? 0 : Math.cbrt(distance) * 150 + 75;
}

export { UnderlineDirections, useDynamicUnderlineStyling };
//# sourceMappingURL=useDynamicUnderlineStyling.js.map
